import { useCallback } from 'react';
import { OpportunityInput } from 'sr-types/lib/opportunity/v1/graphql';
import { OrderInput } from 'sr-types/lib/order/v1/graphql';
import { Reference, ResourceInput } from 'sr-types/lib/production/v1/graphql';
import { CrewHire } from 'sr-types/lib/search/v1/graphql';
import { useCreateApprovalContext } from '../common/approvals';
import { failedToastMessage } from '../common/utils/commonUtils';
import { createResourceForProduction } from '../hiringManager/hireResource';
import { useConfirmResourceOrder } from '../production/resource/orders/hooks/confirmResourceOrder';
import { RESOURCE_ORDERS_TYPE_ID } from '../production/resource/orders/utils/resourceOrdersConstants';
import { useSaveBookingForResource } from '../production/resource/resourceBooking/hooks/saveBookingForResource';

export const PEOPLE = 'people';
export const PERSON = 'person';
export const BOOKING = 'booking';
export const RESOURCE = 'resource';
export const WORKFLOW = 'workflow';

export const useEntitiesToProductionMutations = () => {
    const { saveResourceForProduction } = createResourceForProduction();
    const { saveBookingToResource } = useSaveBookingForResource();
    const { confirmDealForResource } = useConfirmResourceOrder(RESOURCE_ORDERS_TYPE_ID.deal);
    const { saveApprovalInput } = useCreateApprovalContext();

    type AssociateHiredCrewEntitiesParams = {
        productionId: string;
        resInput: ResourceInput;
        bookingInput: OpportunityInput;
        dealInput: OrderInput;
        onCancelStepper: () => void;
        shouldRequestApproval: boolean;
        setCrewHireRow?: (crewHire: CrewHire) => void;
    };

    const associateHiredCrewEntitiesWithProduction = useCallback(
        async ({
            productionId,
            resInput,
            bookingInput,
            dealInput,
            onCancelStepper,
            shouldRequestApproval,
            setCrewHireRow
        }: AssociateHiredCrewEntitiesParams) => {
            onCancelStepper();
            if (shouldRequestApproval) {
                try {
                    await saveApprovalInput(true);
                } catch (error) {
                    failedToastMessage('form.save.failure', { name: WORKFLOW });
                    setCrewHireRow?.(undefined);
                    return;
                }
            }

            return saveResourceForProduction(productionId, resInput)
                .then((resourceData: { id: string; reference: Reference }) => {
                    return saveBookingToResource({
                        resourceId: resourceData.id,
                        input: {
                            ...bookingInput
                        }
                    })
                        .then((opportunityReference) => {
                            dealInput.opportunityReference = opportunityReference;
                            return confirmDealForResource({
                                orderInput: {
                                    ...dealInput
                                }
                            }).catch(() => {
                                failedToastMessage('form.save.failure', {
                                    name: RESOURCE_ORDERS_TYPE_ID.deal
                                });
                                setCrewHireRow?.(undefined);
                            });
                        })
                        .catch(() => {
                            failedToastMessage('form.save.failure', { name: BOOKING });
                            setCrewHireRow?.(undefined);
                        });
                })
                .catch(() => {
                    failedToastMessage('form.save.failure', { name: RESOURCE });
                    setCrewHireRow?.(undefined);
                });
        },
        [confirmDealForResource, saveApprovalInput, saveBookingToResource, saveResourceForProduction]
    );

    return { associateHiredCrewEntitiesWithProduction };
};
